/**
 * The Share-kit image, drawn on a `<canvas>` in the browser (Share & grow).
 *
 * Client-side on purpose: no server render, no image service, no per-image
 * cost. The operator sees exactly the pixels that will be posted, and the PNG
 * never leaves their device until they choose to share it.
 *
 * What goes on it is the OPERATOR's identity, not ours — shop name, their logo
 * if they uploaded one, and their branding colours from
 * `tenants/{tid}/branding/current.colours`. Never a photo of a customer, never
 * a customer's name: the only people-content a moment can carry is an
 * anonymous review quote.
 *
 * Sizes are proportions of the canvas width rather than fixed numbers, so the
 * square post and the 9:16 story are the same composition at two heights.
 */

export type ShareImageFormat = 'square' | 'story';

export const SHARE_IMAGE_SIZES: Record<ShareImageFormat, { width: number; height: number }> = {
  square: { width: 1080, height: 1080 },
  story: { width: 1080, height: 1920 },
};

export interface ShareImageColours {
  readonly background: string;
  readonly foreground: string;
  readonly accent: string;
  readonly accentForeground: string;
}

export interface ShareImageSpec {
  readonly format: ShareImageFormat;
  readonly shopName: string;
  readonly eyebrow: string;
  readonly headline: string;
  readonly body: string;
  /** The app address printed at the foot, e.g. `kilatcarwash.net/sparkle`. */
  readonly footer: string;
  readonly colours: ShareImageColours;
  readonly logoUrl: string | null;
  /** Font family to draw with — the page's own, so the image matches the product. */
  readonly fontFamily: string;
}

/** Load an image the canvas may read back. Resolves `null` on any failure or after a timeout. */
export function loadImage(url: string, timeoutMs = 1500): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    // Without CORS the PNG export would throw; with it, a bucket that does not
    // allow us simply fails to load and the monogram is drawn instead.
    img.crossOrigin = 'anonymous';
    const timer = window.setTimeout(() => resolve(null), timeoutMs);
    img.onload = () => {
      window.clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      resolve(null);
    };
    img.src = url;
  });
}

/**
 * One CJK character (each is its own break opportunity), or a run of other
 * non-space characters, or a run of spaces. Written with escapes so the source
 * holds no ideographic space character.
 */
const CJK = '\\u3000-\\u9fff\\uff00-\\uffef';
const CJK_OR_WORD = new RegExp(`[${CJK}]|[^\\s${CJK}]+|\\s+`, 'g');

/**
 * Break `text` into lines no wider than `maxWidth`.
 *
 * Splits on spaces for Latin and Tamil script, and between characters for
 * Chinese, which has no spaces to split on — a caption in Chinese would
 * otherwise be one line running off the image.
 */
export function wrapText(measure: (s: string) => number, text: string, maxWidth: number): string[] {
  const tokens = text.match(CJK_OR_WORD);
  if (!tokens) return [];
  const lines: string[] = [];
  let line = '';
  const push = () => {
    const trimmed = line.trim();
    if (trimmed) lines.push(trimmed);
    line = '';
  };
  for (const token of tokens) {
    if (/^\s+$/.test(token)) {
      if (line !== '') line += ' ';
      continue;
    }
    if (measure((line + token).trim()) <= maxWidth) {
      line += token;
      continue;
    }
    if (line.trim() !== '') push();
    if (measure(token) <= maxWidth) {
      line = token;
      continue;
    }
    // A single token wider than the line (a long URL, a long word): break it
    // by character rather than letting it run off the image.
    for (const ch of token) {
      if (line !== '' && measure(line + ch) > maxWidth) push();
      line += ch;
    }
  }
  push();
  return lines;
}

/** Fit `text` into at most `maxLines` lines by shrinking from `startSize`. */
function fitText(
  ctx: CanvasRenderingContext2D,
  text: string,
  opts: {
    readonly maxWidth: number;
    readonly maxLines: number;
    readonly startSize: number;
    readonly minSize: number;
    readonly weight: number;
    readonly family: string;
  },
): { size: number; lines: string[] } {
  let size = opts.startSize;
  for (;;) {
    ctx.font = `${opts.weight} ${size}px ${opts.family}`;
    const lines = wrapText((s) => ctx.measureText(s).width, text, opts.maxWidth);
    if (lines.length <= opts.maxLines || size <= opts.minSize) {
      return { size, lines: lines.slice(0, opts.maxLines) };
    }
    size = Math.max(opts.minSize, Math.round(size * 0.9));
  }
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Draw the whole image onto `canvas`, resizing it to the format. */
export async function renderShareImage(
  canvas: HTMLCanvasElement,
  spec: ShareImageSpec,
): Promise<void> {
  const { width: W, height: H } = SHARE_IMAGE_SIZES[spec.format];
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const pad = W * 0.08;
  const family = spec.fontFamily;
  const { background, foreground, accent, accentForeground } = spec.colours;

  // Ground: the operator's primary colour, with a soft accent sweep so the
  // card reads as designed rather than as a flat fill.
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, W, H);
  ctx.save();
  ctx.globalAlpha = 0.22;
  ctx.fillStyle = accent;
  ctx.beginPath();
  ctx.arc(W * 0.95, H * 0.08, W * 0.55, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 0.12;
  ctx.beginPath();
  ctx.arc(W * 0.05, H * 0.98, W * 0.45, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Identity row: logo (or a monogram) and the shop name.
  const mark = W * 0.12;
  const markX = pad;
  const markY = pad;
  const logo = spec.logoUrl ? await loadImage(spec.logoUrl) : null;
  ctx.save();
  roundedRect(ctx, markX, markY, mark, mark, mark * 0.24);
  ctx.fillStyle = foreground;
  ctx.fill();
  ctx.clip();
  if (logo) {
    const scale = Math.min(mark / logo.width, mark / logo.height) * 0.86;
    const lw = logo.width * scale;
    const lh = logo.height * scale;
    ctx.drawImage(logo, markX + (mark - lw) / 2, markY + (mark - lh) / 2, lw, lh);
  } else {
    ctx.fillStyle = background;
    ctx.font = `700 ${Math.round(mark * 0.5)}px ${family}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      Array.from(spec.shopName.trim())[0]?.toUpperCase() ?? '',
      markX + mark / 2,
      markY + mark / 2,
    );
  }
  ctx.restore();

  ctx.fillStyle = foreground;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  const name = fitText(ctx, spec.shopName, {
    maxWidth: W - pad * 2 - mark - W * 0.04,
    maxLines: 1,
    startSize: Math.round(W * 0.045),
    minSize: Math.round(W * 0.03),
    weight: 650,
    family,
  });
  ctx.font = `650 ${name.size}px ${family}`;
  ctx.fillText(name.lines[0] ?? '', markX + mark + W * 0.04, markY + mark / 2);

  // Message block, vertically centred in the space between identity and foot.
  ctx.textBaseline = 'alphabetic';
  const textWidth = W - pad * 2;
  const eyebrowSize = Math.round(W * 0.034);
  const headline = fitText(ctx, spec.headline, {
    maxWidth: textWidth,
    maxLines: spec.format === 'story' ? 6 : 4,
    startSize: Math.round(W * 0.095),
    minSize: Math.round(W * 0.05),
    weight: 750,
    family,
  });
  const body = fitText(ctx, spec.body, {
    maxWidth: textWidth,
    maxLines: 3,
    startSize: Math.round(W * 0.042),
    minSize: Math.round(W * 0.032),
    weight: 500,
    family,
  });
  const headLead = headline.size * 1.12;
  const bodyLead = body.size * 1.35;
  const blockHeight =
    eyebrowSize * 1.8 + headline.lines.length * headLead + W * 0.03 + body.lines.length * bodyLead;
  const top = markY + mark;
  const footTop = H - pad - W * 0.09;
  let y = top + Math.max(W * 0.06, (footTop - top - blockHeight) / 2) + eyebrowSize;

  // Eyebrow as a pill in the accent colour.
  ctx.font = `700 ${eyebrowSize}px ${family}`;
  const eyebrowText = spec.eyebrow.toUpperCase();
  const eyebrowW = ctx.measureText(eyebrowText).width + eyebrowSize * 1.4;
  ctx.fillStyle = accent;
  roundedRect(ctx, pad, y - eyebrowSize * 1.1, eyebrowW, eyebrowSize * 1.6, eyebrowSize * 0.8);
  ctx.fill();
  ctx.fillStyle = accentForeground;
  ctx.fillText(eyebrowText, pad + eyebrowSize * 0.7, y);
  y += eyebrowSize * 0.8 + headLead;

  ctx.fillStyle = foreground;
  ctx.font = `750 ${headline.size}px ${family}`;
  for (const line of headline.lines) {
    ctx.fillText(line, pad, y);
    y += headLead;
  }
  y += W * 0.03 - headLead + bodyLead;
  ctx.font = `500 ${body.size}px ${family}`;
  ctx.save();
  ctx.globalAlpha = 0.88;
  for (const line of body.lines) {
    ctx.fillText(line, pad, y);
    y += bodyLead;
  }
  ctx.restore();

  // Foot: where to go.
  ctx.save();
  ctx.globalAlpha = 0.35;
  ctx.fillStyle = foreground;
  ctx.fillRect(pad, footTop, W - pad * 2, Math.max(2, W * 0.002));
  ctx.restore();
  ctx.fillStyle = foreground;
  const foot = fitText(ctx, spec.footer, {
    maxWidth: textWidth,
    maxLines: 1,
    startSize: Math.round(W * 0.04),
    minSize: Math.round(W * 0.026),
    weight: 650,
    family,
  });
  ctx.font = `650 ${foot.size}px ${family}`;
  ctx.fillText(foot.lines[0] ?? '', pad, footTop + W * 0.065);
}

export function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    } catch {
      // A tainted canvas (a logo served without CORS that still loaded) throws
      // here; the caller re-renders without the logo.
      resolve(null);
    }
  });
}
